import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { countFailed, countPending } from '../lib/db';

export type SyncState = 'synced' | 'pending' | 'failed' | 'offline';

export interface SyncStatus {
  state: SyncState;
  pendingCount: number;
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

/**
 * Drives the persistent sync chip required by CLAUDE.md: Synced / N pending /
 * Sync failed. Counts come from the IndexedDB outbox, so they survive a reload.
 */
export function useSyncStatus(): SyncStatus {
  const online = useOnline();
  const pendingCount = useLiveQuery(countPending, [], 0);
  const failedCount = useLiveQuery(countFailed, [], 0);

  if (failedCount > 0) return { state: 'failed', pendingCount };
  if (!online) return { state: 'offline', pendingCount };
  if (pendingCount > 0) return { state: 'pending', pendingCount };
  return { state: 'synced', pendingCount };
}
